import React, { useState } from 'react';
import { AlertCircle, CheckCircle, UploadCloud } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { uploadDocuments } from '../../services/upload';
import Modal from '../ui/Modal';

const UploadModal = ({ isOpen, onClose }) => {
  const { token, theme } = useAppContext();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | uploading | success | error
  const [message, setMessage] = useState('');

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
    setMessage('');
    setStatus('idle');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!token) {
      setMessage('Authentication token missing.');
      setStatus('error');
      return;
    }
    if (!selectedFiles.length) {
      setMessage('Please select at least one file to upload.');
      setStatus('error');
      return;
    }

    setStatus('uploading');
    setMessage('');
    try {
      const result = await uploadDocuments(selectedFiles, token);
      const names = (result?.uploaded_files || []).join(', ');
      setMessage(result?.message || 'Upload completed.');
      if (names) {
        setMessage((prev) => `${prev} Files: ${names}`);
      }
      setStatus('success');
      setSelectedFiles([]);
    } catch (err) {
      setMessage(err.message || 'Upload failed.');
      setStatus('error');
    }
  };

  const badgeClasses =
    status === 'success'
      ? 'text-green-600'
      : status === 'error'
      ? 'text-red-600'
      : 'text-gray-500';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upload & Index Documents" maxWidthClass="max-w-xl">
      <form className="space-y-4" onSubmit={handleUpload}>
        <div
          className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center gap-3 ${
            theme === 'dark'
              ? 'border-gray-700 bg-gray-900 text-gray-100'
              : 'border-gray-300 bg-gray-50 text-gray-800'
          }`}
        >
          <UploadCloud className="w-8 h-8 text-blue-500" />
          <p className="text-sm text-center">
            Select one or more documents to upload and index. Supported types follow backend configuration.
          </p>
          <label
            className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            Choose files
            <input type="file" multiple className="hidden" onChange={handleFileChange} />
          </label>
          {selectedFiles.length > 0 && (
            <div className="w-full text-xs text-left mt-2 space-y-1">
              {selectedFiles.map((file) => (
                <div key={file.name} className="truncate">
                  {file.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className={`flex items-center gap-2 text-xs ${badgeClasses}`}>
            {status === 'success' && <CheckCircle size={16} />}
            {status === 'error' && <AlertCircle size={16} />}
            {message && <span>{message}</span>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className={`px-3 py-2 rounded-md text-sm ${
                theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-gray-200 text-gray-800'
              }`}
            >
              Close
            </button>
            <button
              type="submit"
              disabled={status === 'uploading'}
              className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              {status === 'uploading' ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default UploadModal;
